import React, { useState, useEffect, useCallback, useRef } from "react";
import io from "socket.io-client";
import { GoDotFill } from "react-icons/go";
import DashboardServices from "../../../services/DashboardServices";

const ONLINE_POLL_MS = 25000;

const ROLE_MODAL_KEYS = {
  // admin: { title: "Online Admin", listKey: "admin" },
  outlet: { title: "Online Outlet", listKey: "outlet" },
  mobile: { title: "Online Mobile", listKey: "mobile" },
};

function formatOnlineUserLine(u) {
  const name = (u?.name || "").trim() || "—";
  const un = (u?.username || "").trim() || "—";
  return `${name} [${un}]`;
}

const DashboardSuperAdmin = ({ data }) => {
  const stats = data?.stats || {};
  const settings = data?.settings;
  const recentWinners = data?.recentWinners || [];

  const [gameStatus, setGameStatus] = useState({
    game_3min: true,
    game_5min: true,
  });

  const [onlinePayload, setOnlinePayload] = useState({
    counts: { admin: 0, outlet: 0, mobile: 0 },
    users: { admin: [], outlet: [], mobile: [] },
  });
  const [onlineModalRole, setOnlineModalRole] = useState(null);
  const onlineAbortRef = useRef(null);

  const loadOnlineUsers = useCallback(async () => {
    try {
      onlineAbortRef.current?.abort();
      const ac = new AbortController();
      onlineAbortRef.current = ac;
      const res = await DashboardServices.getOnlineUsersByRole({
        signal: ac.signal,
      });
      if (res?.isSuccess && res?.data) {
        setOnlinePayload({
          counts: res.data.counts || {
            admin: 0,
            outlet: 0,
            mobile: 0,
          },
          users: res.data.users || {
            admin: [],
            outlet: [],
            mobile: [],
          },
        });
      }
    } catch (e) {
      if (e?.name === "AbortError" || e?.code === "ERR_CANCELED") return;
      console.error("loadOnlineUsers:", e);
    }
  }, []);

  useEffect(() => {
    loadOnlineUsers();
    const t = setInterval(loadOnlineUsers, ONLINE_POLL_MS);
    return () => {
      clearInterval(t);
      onlineAbortRef.current?.abort();
    };
  }, [loadOnlineUsers]);

  useEffect(() => {
    if (settings && (settings.game_3min !== undefined || settings.game_5min !== undefined)) {
      setGameStatus((prev) => ({
        ...prev,
        ...(settings.game_3min !== undefined && { game_3min: !!settings.game_3min }),
        ...(settings.game_5min !== undefined && { game_5min: !!settings.game_5min }),
      }));
    }
  }, [data?.settings]);

  useEffect(() => {
    const s = io(process.env.REACT_APP_NODE_URL, { transports: ["websocket"] });
    s.on("gameStatusChange", (payload) => {
      if (payload && (payload.game_3min !== undefined || payload.game_5min !== undefined)) {
        setGameStatus((prev) => ({
          ...prev,
          ...(payload.game_3min !== undefined && { game_3min: !!payload.game_3min }),
          ...(payload.game_5min !== undefined && { game_5min: !!payload.game_5min }),
        }));
      }
    });
    return () => s.disconnect();
  }, []);

  const modalMeta = onlineModalRole ? ROLE_MODAL_KEYS[onlineModalRole] : null;
  const modalList =
    modalMeta && onlinePayload?.users
      ? onlinePayload.users[modalMeta.listKey] || []
      : [];

  return (
    <>
      <div className="dashboard-grid">
        <div className="stat-card">
          <div className="stat-icon">🔥</div>
          <div className="stat-content">
            <h3>Running Sessions</h3>
            <p className="stat-game-value">
              3min{" "}
              <span className={gameStatus.game_3min ? "live" : "stopped"}>
                <GoDotFill />
                {gameStatus.game_3min ? "Live" : "Stopped"}
              </span>
            </p>
            <p className="stat-game-value">
              5min{" "}
              <span className={gameStatus.game_5min ? "live" : "stopped"}>
                <GoDotFill />
                {gameStatus.game_5min ? "Live" : "Stopped"}
              </span>
            </p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h3>Today Sessions</h3>
            <p className="stat-value">{stats.todaySessionsCount ?? 0}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🏆</div>
          <div className="stat-content">
            <h3>Today Winners</h3>
            <p className="stat-value">{stats.todayWinnersCount ?? 0}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>Total Users</h3>
            <p className="stat-value">{stats.totalUsersCount ?? 0}</p>
          </div>
        </div>
      </div>

      <div className="dashboard-section dashboard-section--online">
        <h2 className="dashboard-section-title">Online (live session)</h2>
        <div className="dashboard-grid--online">
          {/* <button
            type="button"
            className="stat-card stat-card--clickable"
            onClick={() => setOnlineModalRole("admin")}
          >
            <div className="stat-icon">🛡️</div>
            <div className="stat-content">
              <h3>Online Admin</h3>
              <p className="stat-value">{onlinePayload.counts.admin ?? 0}</p>
            </div>
          </button> */}
          <button
            type="button"
            className="stat-card stat-card--clickable"
            onClick={() => setOnlineModalRole("outlet")}
          >
            <div className="stat-icon">🏪</div>
            <div className="stat-content">
              <h3>Online Outlet</h3>
              <p className="stat-value">{onlinePayload.counts.outlet ?? 0}</p>
            </div>
          </button>
          <button
            type="button"
            className="stat-card stat-card--clickable"
            onClick={() => setOnlineModalRole("mobile")}
          >
            <div className="stat-icon">📱</div>
            <div className="stat-content">
              <h3>Online Mobile</h3>
              <p className="stat-value">{onlinePayload.counts.mobile ?? 0}</p>
            </div>
          </button>
        </div>
      </div>

      {modalMeta && (
        <div
          className="dashboard-online-modal-backdrop"
          role="presentation"
          onClick={() => setOnlineModalRole(null)}
        >
          <div
            className="dashboard-online-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="online-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dashboard-online-modal-head">
              <h3 id="online-modal-title">{modalMeta.title}</h3>
              <button
                type="button"
                className="dashboard-online-modal-close"
                onClick={() => setOnlineModalRole(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <ul className="dashboard-online-modal-list">
              {modalList.length === 0 ? (
                <li className="dashboard-online-modal-empty">
                  No users online in this role.
                </li>
              ) : (
                modalList.map((u) => (
                  <li key={u.user_id}>{formatOnlineUserLine(u)}</li>
                ))
              )}
            </ul>
          </div>
        </div>
      )}

      {settings && (
        <div className="dashboard-section">
          <h2>Settings Summary</h2>
          <div className="settings-summary">
            <span>Yantra Amount: {settings.yantra_amount}</span>
            <span>Winning Amount: {settings.winning_amount}</span>
            <span>Difference: {settings.difference}</span>
            <span>3min: {gameStatus.game_3min ? "On" : "Off"}</span>
            <span>5min: {gameStatus.game_5min ? "On" : "Off"}</span>
          </div>
        </div>
      )}

      <div className="recent-activity">
        <h2>Recent Winner Sessions</h2>
        <div className="activity-list">
          {recentWinners.length === 0 ? (
            <p className="activity-empty">No recent winner sessions.</p>
          ) : (
            recentWinners.map((w) => (
              <div className="activity-item" key={w.id}>
                <span className="activity-time">
                  {w.end_time ? new Date(w.end_time).toLocaleString() : "—"}
                </span>
                <span className="activity-desc">
                  Session #{w.session_no} ({w.game_type}) —
                  {w.winner_type === "JACKPOT"
                    ? "Multi Winner-"
                    : w.winner_type === "JACKPOTX"
                      ? `Jackpot(${w.winner_x})-`
                      : ""}
                  {w.winner_yantra.length > 0 ? w.winner_yantra.join(" | ") : ""}
                </span>
                <span className="activity-result won">
                  ₹{Number(w.winning_amount || 0).toFixed(2)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default DashboardSuperAdmin;
